import { AppBar, Grid, Toolbar, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	EmptyList,
	EnhancedSnackbar,
} from "@websential/cosmic-v20";
import {
	EMPLOYEE_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { GET_QUERY_KEY, QUERY_OPTIONS } from "../../../common/constants";
import { IResponse, IServerError } from "../../../models/common";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {

	formatDate,
	sortDDLByLabel,
	useNavigateToLogout,
	useRemoveQueriesOnUnmount,
} from "../../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AuthService } from "../../../services";
import { CRACreditService } from "../../../services/CRACreditService";
import { CompanyTD1FormService } from "../../../services/CompanyTD1FormService";
import { DateRange } from "@mui/icons-material";
import { DepartmentsService } from "../../../services/DepartmentsService";
import { EmployeeService } from "../../../services/EmployeeService";
import { ExpenseCategoryService } from "../../../services/ExpenseCategoryService";
import FileSaver from "file-saver";
import { IAppState } from "../../../store/reducers";
import { IDropDownListResponse } from "../../../models/common/IDropDownListResponse";
import { IGetListRequest } from "../../../models";
import { LookupFieldService } from "../../../services/LookupFieldService";
import { PayrollReportDataGridWithColumnToggled } from "./PayrollReportDataGridWithColumnToggled";
import { PayrollReportHeader } from "./PayrollReportHeader";
import { ReportsService } from "../../../services/ReportsService";
import { TeamsService } from "../../../services/TeamsService";
import { endOfDay } from "date-fns";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { useNavigate } from "react-router-dom";

export interface IPayrollReportSetupPage { }
const PAYROLL_REPORT_QUERY_KEY = "payroll-report";
const EMPLOYEE_DDL_DATA_QUERY_KEY = "employee-ddl-data-record";


const usePayrollReportList = (
	runGetListAPICall: boolean,
	clientId: string,
	taxYear: number,
	startDate: string | null,
	endDate: string | null,
	groupBy: number,
	paymentStatus: string,
	expenseCategory: string,
	paymentMethod: string,
	provinceOfEmployment: string,
	reportDate: number,
	payPeriod: string,
	payPeriodEndDate: string,
	department: string,
	team: string,
	employee: string,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void,
) =>
	useQuery({
		queryKey: [
			PAYROLL_REPORT_QUERY_KEY,
			runGetListAPICall,
			clientId,
			taxYear,
			startDate,
			endDate,
			groupBy,
			paymentStatus,
			expenseCategory,
			paymentMethod,
			provinceOfEmployment,
			reportDate,
			payPeriod,
			payPeriodEndDate,
			department,
			team,
			employee,
			params
		],
		queryFn: () =>
			ReportsService.getPayrollReport(
				runGetListAPICall,
				clientId,
				taxYear,
				startDate,
				endDate,
				groupBy,
				paymentStatus,
				expenseCategory,
				paymentMethod,
				provinceOfEmployment,
				reportDate,
				payPeriod,
				payPeriodEndDate,
				department,
				team,
				employee,
				params,
				navigateToLogout
			),
		refetchOnWindowFocus: false,
	});

const useGetEmployeeDDL = (clientId: string) =>
	useQuery({
		queryKey: ["employee-status-ddl", clientId],
		queryFn: () => {
			return EmployeeService.getEmployeeDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});
const useDepartmentsDDL = (
	clientId: string,
) =>
	useQuery({
		queryKey: ["departments-list"],
		queryFn: (data) => {

			return DepartmentsService.getGetDepartmentsDDL(
				clientId,
			);
		},
		refetchOnWindowFocus: false,
	});


const useGetTeamsDDL = (clientId: string) =>
	useQuery({
		queryKey: ["teams-ddl", clientId],
		queryFn: () => {
			return TeamsService.getGetTeamsDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});


const useGetExpenseCategoryDDL = (clientId: string) =>
	useQuery({
		queryKey: ["expense-category-ddl", clientId],
		queryFn: () => {
			return ExpenseCategoryService.getGetExpenseCategoryDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});


const useGetEmployeeBasedProvincesOfEmploymentDDL = (clientId: string,) =>
	useQuery({
		queryKey: ["province-of-employment-ddl"],
		queryFn: (data) => {
			return ReportsService.getEmployeeBasedProvincesOfEmploymentDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});

const useGetPayPeriodEndDateDDL = (
	clientId: string,
	employeePayPeriodId: string | null,
	taxYear: number
) =>
	useQuery({
		queryKey: [
			"periods-ddl",
			employeePayPeriodId,
		],
		queryFn: () => {
			return CRACreditService.getPayPeriodDDL(
				clientId,
				employeePayPeriodId,
				taxYear
			);
		},
		refetchOnWindowFocus: false,
	});
const useGetPayFrequencyDDL = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: ["pay-frequency-ddl"],
		queryFn: (data) => {
			return EmployeeService.getPayFrequencyDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});

export const PayrollReport: React.FC<IPayrollReportSetupPage> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const clientName = useSelector((state: IAppState) => state.client.clientName);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();

	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isLoading, setIsLoading] = useState(false);
	const [payrollReportData, setPayrollReportData] =
		useState<any>(undefined);
	const [runGetListAPICall, setRunGetListAPICall] =
		useState<any>(false);
	const [employeeStatus, setEmployeeStatus] = useState<number>(100);

	const [selectedPeriodEndDate, setSelectedPeriodEndDate] = useState<string | null>(null);

	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});


	const groupBy = formMethods.watch("groupBy");
	const reportDate = formMethods.getValues("reportDate");
	const employee = formMethods.getValues("employee");
	const paymentStatus = formMethods.getValues("paymentStatus");
	const payPeriod = formMethods.getValues("payPeriod");
	const startDate = formMethods.getValues("startDate");
	const expenseCategory = formMethods.getValues("expenseCategory");
	const endDate = formMethods.getValues("endDate");
	const paymentMethod = formMethods.getValues("paymentMethod");
	const payPeriodEndDate = formMethods.getValues("payPeriodEndDate");
	const department = formMethods.getValues("department");
	const provinceOfEmployment = formMethods.getValues("provinceOfEmployment");
	const team = formMethods.getValues("team");

	const { isPending, data } = usePayrollReportList(
		runGetListAPICall,
		clientId,
		taxYear,
		formatDate(startDate),
		formatDate(endDate),
		groupBy,
		paymentStatus,
		expenseCategory,
		paymentMethod,
		provinceOfEmployment,
		reportDate,
		payPeriod,
		payPeriodEndDate,
		department,
		team,
		employee,
		queryParams,
		navigateToLogout
	);
	const payrollReportListData: any | null = data;
	useEffect(() => {
		if (payrollReportListData) {
			setPayrollReportData(payrollReportListData);
		}
	}, [payrollReportListData]);

	// const { isPending: isPendingEmployeeDDLItems, data: employeeDDL } =
	// useGetEmployeeDDLItems(clientId, employeeStatus, taxYear);

	const { isLoading: isDepartmentLoadingDDL, data: departmentDDL } = useDepartmentsDDL(clientId);
	const { isLoading: isTeamLoadingDDL, data: teamsDDL } = useGetTeamsDDL(clientId);
	const { isLoading: isExpenseCategoryLoadingDDL, data: expenseCategoryDDL } = useGetExpenseCategoryDDL(clientId);
	const { isLoading: isProvinceOfEmpLoadingDDL, data: provinceOfEmploymentDDL } = useGetEmployeeBasedProvincesOfEmploymentDDL(clientId);
	const { isLoading: isEmployeeLoadingDDL, data: employeeDDL } = useGetEmployeeDDL(clientId);
	const { isLoading: isPayPeriodLoadingDDL, data: payPeriodDDL } = useGetPayFrequencyDDL(clientId, taxYear);
	const { isLoading: isPayPeriodEndDateLoadingDDL, data: payPeriodEndDateDDL } = useGetPayPeriodEndDateDDL(clientId, selectedPeriodEndDate, taxYear);

	const employeeDDLItems = useMemo(() => {
		if (employeeDDL) {
			if (
				employeeDDL &&
				Array.isArray(employeeDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(employeeDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [employeeDDL]);

	const departmentsDDLItems = useMemo(() => {
		if (departmentDDL) {
			if (
				departmentDDL &&
				Array.isArray(departmentDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(departmentDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [departmentDDL]);

	const teamsDDLItems = useMemo(() => {
		if (teamsDDL) {
			if (
				teamsDDL &&
				Array.isArray(teamsDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(teamsDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [teamsDDL]);

	const expenseCategoryDDLItems = useMemo(() => {
		if (expenseCategoryDDL) {
			if (
				expenseCategoryDDL &&
				Array.isArray(expenseCategoryDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(expenseCategoryDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [expenseCategoryDDL]);

	const provinceOfEmploymentDDLItems = useMemo(() => {
		if (provinceOfEmploymentDDL) {
			if (
				provinceOfEmploymentDDL &&
				Array.isArray(provinceOfEmploymentDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(provinceOfEmploymentDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [provinceOfEmploymentDDL]);

	const payPeriodDDLItems = useMemo(() => {
		if (payPeriodDDL) {
			if (
				payPeriodDDL &&
				Array.isArray(payPeriodDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(payPeriodDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [payPeriodDDL]);

	const payPeriodEndDateDDLItems = useMemo(() => {
		if (payPeriodEndDateDDL) {
			if (
				payPeriodEndDateDDL &&
				Array.isArray(payPeriodEndDateDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(payPeriodEndDateDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [payPeriodEndDateDDL]);

	//   after click apply button api calls with updated values and update payroll report data
	const payrollReportDataAPICall = (
		startDate: string | null,
		endDate: string | null,
		groupBy: number,
		paymentStatus: string,
		expenseCategory: string,
		paymentMethod: string,
		provinceOfEmployment: string,
		reportDate: number,
		payPeriod: string,
		payPeriodEndDate: string,
		department: string,
		team: string,
		employee: string,
	) => {
		const fetchData = async () => {
			setIsLoading(true);

			try {
				const response: any = await ReportsService.getPayrollReport(
					true,
					clientIdStore,
					taxYear,
					formatDate(startDate),
					formatDate(endDate),
					groupBy,
					paymentStatus,
					expenseCategory,
					paymentMethod,
					provinceOfEmployment,
					reportDate,
					payPeriod,
					payPeriodEndDate,
					department,
					team,
					employee,
					queryParams,
					navigateToLogout
				);
				if (response) {
					setPayrollReportData(response);
				}
			} catch (error) {
				console.error(error);
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	};

	const onSubmit = (values: any) => {
		payrollReportDataAPICall(
			values.startDate,
			values.endDate,
			values.groupBy,
			values.paymentStatus,
			values.expenseCategory,
			values.paymentMethod,
			values.provinceOfEmployment,
			values.reportDate,
			values.payPeriod,
			values.payPeriodEndDate,
			values.department,
			values.team,
			values.employee,
		);
	};

	const [zeroEmployee, setzeroEmployee] = useState(true);

	const [submitAddRemoveColumnsHandler, setSubmitAddRemoveColumnsHandler] =
		useState(undefined);
	const [onColumnReorder, setOnColumnReorder] = useState(undefined);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		setRunGetListAPICall(true)
	}, []);

	const {
		isPending: isSubmittingAddRemoveHeaderColumns,
		mutate: submitAddRemoveHeaderColumns,
	} = useMutation({
		mutationFn: ReportsService.getPayrollReportAddRemoveHeaderColumns,
		onSuccess: (res: any | IServerError | IResponse) => {
			if (res.status === 200) {
				setRunGetListAPICall(true)
				return queryClient.invalidateQueries({
					queryKey: [PAYROLL_REPORT_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message
						? res.message
						: "Something went wrong, try again later!",
					severity: "error",
				});
			}
		},
		onError: (err: any) => {
			setSnackbarData({
				open: true,
				message: err.message,
				severity: "error",
			});
		},
	});
	useEffect(() => {
		if (submitAddRemoveColumnsHandler) {
			submitAddRemoveHeaderColumns({
				clientId: clientId,
				columns: submitAddRemoveColumnsHandler,
				navigateToLogout: navigateToLogout,
			});
		}
	}, [submitAddRemoveColumnsHandler]);

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	useRemoveQueriesOnUnmount(["employee-report"]);

	const upadtedPayrollReportListItems = useMemo(() => {
		return (
			payrollReportData &&
			payrollReportData.items &&
			payrollReportData?.items.map((item: any) => ({
				...item,
			}))
		);
	}, [payrollReportData]);

	const expandedLabels = ["Reports"];
	const enhanceData = (data: any[], ancestorChain: string[] = []): any[] => {
		return data.map((item) => {
			const currentChain = [...ancestorChain, item.label];
			const isPayrollUnderReports =
				item.label === "Payroll" && ancestorChain.includes("Reports");
			const expandedPayrollReportOrSetupItem =
				item.label === "Payroll Report" &&
				ancestorChain.includes("Reports") &&
				ancestorChain.includes("Payroll");

			const isExpanded =
				expandedLabels.includes(item.label) ||
				isPayrollUnderReports ||
				expandedPayrollReportOrSetupItem;

			return {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items
					? enhanceData(item.items, currentChain)
					: undefined,
				path: expandedPayrollReportOrSetupItem && expandedPayrollReportOrSetupItem
					? "/reports/payroll"
					: item.path ?? undefined,
			};
		});
	};

	// getting latest updated values of form fields using watch method
	const { watch } = formMethods;
	const values = watch([
		"groupBy",
		"reportDate",
		"employee",
		"paymentStatus",
		"payPeriod",
		"startDate",
		"expenseCategory",
		"endDate",
		"paymentMethod",
		"payPeriodEndDate",
		"department",
		"provinceOfEmployment",
		"team",
	]);

	const savePdfAsFile = (xmlData: any, filename: any) => {
		const blob = new Blob([xmlData], { type: "pdf;charset=utf-8" });
		FileSaver.saveAs(blob, filename);
	};

	const onExportButtonClick = async (selectedOption: string) => {
		const [
			groupBy,
			reportDate,
			employee,
			paymentStatus,
			payPeriod,
			startDate,
			expenseCategory,
			endDate,
			paymentMethod,
			payPeriodEndDate,
			department,
			provinceOfEmployment,
			team,
		] = values;

		try {
			setIsLoading(true);
			const response = await ReportsService.exportPayrollReport(
				selectedOption,
				clientIdStore,
				taxYear,
				formatDate(startDate),
				formatDate(endDate),
				groupBy,
				paymentStatus,
				expenseCategory,
				paymentMethod,
				provinceOfEmployment,
				reportDate,
				payPeriod,
				payPeriodEndDate,
				department,
				team,
				employee,
				queryParams,
			);

			if (response) {
				if (selectedOption === "PDF") {
					if (response.status === 400) {
						setSnackbarData({
							open: true,
							message: response.data.message
								? response.data.message
								: "Something went wrong.",

							severity: "error",
						});
					} else {
						const filename = `${clientName} - Payroll Report.pdf`;
						savePdfAsFile(response, filename);
					}
				} else if (selectedOption === "XLS") {
					if (response.status === 400) {
						setSnackbarData({
							open: true,
							message: response.data.message
								? response.data.message
								: "Something went wrong.",

							severity: "error",
						});
					} else {
						const filename = `${clientName} - Payroll Report.xlsx`;
						savePdfAsFile(response, filename);
					}
				} else {
					const fileType =
						selectedOption === "XLS"
							? "application/vnd.ms-excel"
							: "text/csv;charset=utf-8;";
					const fileExtension = selectedOption === "XLS" ? "xls" : "csv";

					const blob = new Blob([response], { type: fileType });
					FileSaver.saveAs(blob, `${clientName} - Payroll Report.${fileExtension}`);
				}
			} else {
				console.error("Error: No data received from the server");
			}
		} catch (err) {

			setIsLoading(false);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: EMPLOYEE_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));

		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);


	const image =
		"https://taxslips.blob.core.windows.net/taxslips-public-images-container/empty_employee_list.png";

	return (
		<>
			{/* <h3>Payroll Report</h3> */}
			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"Payroll Report"}
			</div>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<PayrollReportHeader
						// onApplyButtonClickedHandler={onApplyButtonClickedHandler}
						setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)}
						employeeDDLItems={employeeDDLItems}
						departmentsDDL={departmentsDDLItems}
						teamsDDL={teamsDDLItems}
						expenseCategoryDDLItems={expenseCategoryDDLItems}
						provinceOfEmploymentDDLItems={provinceOfEmploymentDDLItems}
						payPeriodDDLItems={payPeriodDDLItems}
						payPeriodEndDateDDLItems={payPeriodEndDateDDLItems}
						onPeriodEndDateSelectionChangeHandler={(value: string) => setSelectedPeriodEndDate(value)}
						taxYear={taxYear}
					/>
				</form>
			</FormProvider>

			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						isPending ||
						isSubmittingAddRemoveHeaderColumns ||
						isLoading ||
						isDepartmentLoadingDDL ||
						isTeamLoadingDDL ||
						isExpenseCategoryLoadingDDL ||
						isProvinceOfEmpLoadingDDL ||
						isEmployeeLoadingDDL ||
						isPayPeriodLoadingDDL || isPayPeriodEndDateLoadingDDL

					}
					testId="payroll-report"
				/>

				<PayrollReportDataGridWithColumnToggled
					columns={
						payrollReportData?.headerColumns
							? payrollReportData.headerColumns.filter(
								(item: any) => item.field !== "id"
							)
							: []
						// payrollReportData?.headerColumns ?? []
					}
					completeHeaderColumnsList={
						payrollReportData
							&& payrollReportData
								?.totalHeaderColumns &&
							Object.keys(payrollReportData.totalHeaderColumns).length !== 0
							? payrollReportData.totalHeaderColumns
							: {}
						// payrollReportData?.headerColumns ?? []
					}
					submitAddRemoveColumnsHandler={(value: any) =>
						setSubmitAddRemoveColumnsHandler(value)
					}
					onColumnReorder={(value: any) => setOnColumnReorder(value)}
					rows={
						payrollReportData && upadtedPayrollReportListItems
							? upadtedPayrollReportListItems
							: []
						// payrollReportData?.items ?? []
						// itams
					}
					lastRows={
						payrollReportData &&
						payrollReportData?.items && payrollReportData?.items.length &&
						payrollReportData.employeeContribution &&
						payrollReportData.employerContribution &&
						payrollReportData.craRemittance &&
						payrollReportData.rqRemittance &&
						[payrollReportData.employeeContribution, payrollReportData.employerContribution, payrollReportData.craRemittance, payrollReportData.rqRemittance]
					}
					showIndexColumn={true}
					showChecboxColumn={false}
					showSettingColumn={false}
					// onImportButtonClickedHandler={onImportFileClicked}
					// onImportExcelButtonClickedHandler={onExcelToPdfClicked}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						payrollReportData && payrollReportData.total
							? payrollReportData.total
							: 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					searchFieldPlaceholder={"Search by Name"}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroEmployee(value)
					}
					pageTitle={"payroll Report"}
					showRowExpandedView={true}
					onExportButtonClick={onExportButtonClick}
				/>

				{payrollReportData ? (
					<AppBar
						position="sticky"
						color="default"
						elevation={1}
						sx={{
							backgroundColor: "#F7F7F7",

							boxShadow: "none",
							alignItems: "center",
							zIndex: 0,
							position: "sticky",
							bottom: 0,
						}}
					></AppBar>
				) : (
					<></>
				)}
			</Grid>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
